import React, { useState, useMemo } from 'react';
import {
  Layout,
  Typography,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Tabs,
  Modal,
  Form,
  Select,
  Switch,
  message,
  Tooltip,
  Popconfirm,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SafetyOutlined,
  ShopOutlined,
  CodeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import type { ColumnsType } from 'antd/es/table';
import { Upload } from 'antd';

const { Title } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'agent' | 'sales_agent' | 'developer';
  extension?: string;
  ringcentral_extension?: string;
  agent_level?: number;
  is_active: boolean;
  can_edit_businesses?: boolean;
  departments?: Department[];
}

const UsersAntd: React.FC = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [deptForm] = Form.useForm();

  // State
  const [activeTab, setActiveTab] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [createDeptModal, setCreateDeptModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [confirmBulkText, setConfirmBulkText] = useState('');

  // State for pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // State for filters
  const [filters, setFilters] = useState({
    role: undefined,
    agent_level: undefined,
    is_active: undefined,
  });

  // Fetch users with pagination and filters
  const { data: usersData, isLoading: loadingUsers } = useQuery(
    ['users', pagination.current, pagination.pageSize, filters, searchTerm],
    () => {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
      });
      
      if (filters.role) params.append('role', filters.role);
      if (filters.agent_level) params.append('agent_level', filters.agent_level);
      if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      return api.get(`/api/users?${params.toString()}`).then(res => res.data);
    },
    { 
      enabled: currentUser?.role === 'admin',
      keepPreviousData: true
    }
  );

  const users = usersData?.users || [];
  const total = usersData?.total || 0;

  // Fetch departments
  const { data: departmentsData, isLoading: loadingDepts } = useQuery(
    'departments',
    () => api.get('/api/departments').then(res => res.data),
    { enabled: currentUser?.role === 'admin' }
  );

  const departments = departmentsData?.departments || [];

  // Mutations
  const createUserMutation = useMutation(
    (userData: any) => api.post('/api/users', userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User created successfully');
        setCreateUserModal(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create user');
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.put(`/api/users/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User updated successfully');
        setEditUserModal(false);
        setSelectedUser(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update user');
      }
    }
  );

  const deleteUserMutation = useMutation(
    (id: string) => api.delete(`/api/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('User deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete user');
      }
    }
  );

  const [passwordResetConfirm, setPasswordResetConfirm] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null,
  });

  const sendPasswordResetMutation = useMutation(
    (userId: string) => api.post('/api/auth/admin/send-password-reset', { user_id: userId }),
    {
      onSuccess: (response: any) => {
        const data = response.data;
        setPasswordResetConfirm({ open: false, user: null });
        if (data.emailSent) {
          message.success(`Password reset link sent to ${data.message.split(' ').pop()}`);
        } else {
          message.warning(`Password reset link generated but email could not be sent. Check server logs for the reset link.`);
        }
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to send password reset link');
      }
    }
  );

  const handleSendPasswordReset = (user: User) => {
    setPasswordResetConfirm({ open: true, user });
  };

  const confirmSendPasswordReset = () => {
    if (passwordResetConfirm.user) {
      sendPasswordResetMutation.mutate(passwordResetConfirm.user.id);
    }
  };

  const bulkDeleteUsers = useMutation(
    async (ids: string[]) => {
      await api.post('/api/users/bulk-delete', { ids });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        message.success('Selected users deleted');
        setSelectedRowKeys([]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to bulk delete users');
      }
    }
  );

  const createDeptMutation = useMutation(
    (deptData: any) => api.post('/api/departments', deptData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('departments');
        message.success('Department created successfully');
        setCreateDeptModal(false);
        deptForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create department');
      }
    }
  );

  // Table columns for users
  const userColumns: ColumnsType<User> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          {record.role === 'admin' ? (
            <SafetyOutlined style={{ color: '#f44336' }} />
          ) : record.role === 'sales_agent' ? (
            <ShopOutlined style={{ color: '#ff9800' }} />
          ) : record.role === 'developer' ? (
            <CodeOutlined style={{ color: '#9c27b0' }} />
          ) : (
            <UserOutlined style={{ color: '#1976d2' }} />
          )}
          <span style={{ fontWeight: 600 }}>
            {record.first_name} {record.last_name}
          </span>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        const colors = {
          admin: 'red',
          agent: 'blue',
          sales_agent: 'orange',
          developer: 'purple',
        };
        return (
          <Tag color={colors[role as keyof typeof colors]}>
            {role.toUpperCase().replace('_', ' ')}
          </Tag>
        );
      },
    },
    {
      title: 'Level',
      dataIndex: 'agent_level',
      key: 'agent_level',
      width: 80,
      align: 'center',
      render: (level) => level || '-',
    },
    {
      title: 'Can Edit Businesses',
      dataIndex: 'can_edit_businesses',
      key: 'can_edit_businesses',
      width: 150,
      align: 'center',
      render: (value) => value ? 'Yes' : 'No',
    },
    {
      title: 'Departments',
      key: 'departments',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const depts = record.departments || [];
        if (depts.length === 0) return '-';
        
        const deptNames = depts.map(d => d.name).join(', ');
        return (
          <Tooltip title={deptNames}>
            <Badge count={depts.length} showZero={false} style={{ backgroundColor: '#1976d2' }} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Send Password Reset Link' : 'Admin only'}>
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleSendPasswordReset(record)}
              disabled={currentUser?.role !== 'admin' || !record.email}
              size="small"
            />
          </Tooltip>
          <Tooltip title={currentUser?.role === 'admin' ? 'Delete' : 'Admin only'}>
            <Popconfirm
              title="Delete user"
              description="Are you sure you want to delete this user?"
              onConfirm={() => deleteUserMutation.mutate(record.id)}
              disabled={currentUser?.role !== 'admin'}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={currentUser?.role !== 'admin'}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Department columns
  const deptColumns: ColumnsType<Department> = [
    {
      title: 'Department Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => desc || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  // Since search is now server-side, we don't need client-side filtering
  // But we keep this for any additional client-side filtering if needed
  const filteredUsers = users;

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    form.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      agent_level: user.agent_level,
      department_ids: (user.departments || []).map(d => d.id),
      ringcentral_extension: user.ringcentral_extension,
      can_edit_businesses: user.can_edit_businesses || false,
      is_active: user.is_active,
    });
    setEditUserModal(true);
  };

  const handleCreateUser = (values: any) => {
    createUserMutation.mutate(values);
  };

  const handleUpdateUser = (values: any) => {
    if (selectedUser) {
      const { password, ...data } = values;
      const updateData = password ? values : data;
      updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
    }
  };

  const handleCreateDept = (values: any) => {
    createDeptMutation.mutate(values);
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#fff' }}>
        <Content style={{ padding: 24 }}>
          <Title level={4}>Access Denied</Title>
          <p>You don't have permission to view this page.</p>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Content style={{ padding: 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>Users</Title>
          <Space>
            {activeTab === '1' && (
              <>
                <Button icon={<DownloadOutlined />}>Export</Button>
                <Button danger disabled={selectedRowKeys.length === 0} onClick={() => { setConfirmBulkText(''); setConfirmBulkOpen(true); }}>Bulk Delete</Button>
                <Button icon={<UploadOutlined />} onClick={() => setBulkUploadOpen(true)}>Bulk Upload</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateUserModal(true)}>
                  Add User
                </Button>
              </>
            )}
            {activeTab === '2' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDeptModal(true)}>
                Add Department
              </Button>
            )}
          </Space>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`User Management (${users.length})`} key="1">
            {/* Search */}
            <Input
              placeholder="Search users by name, email, phone..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              style={{ marginBottom: 16, maxWidth: 400 }}
              allowClear
            />

            {/* Filter Controls */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Select
                placeholder="Filter by Role"
                value={filters.role}
                onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="agent">Agent</Select.Option>
                <Select.Option value="developer">Developer</Select.Option>
                <Select.Option value="sales_agent">Sales Agent</Select.Option>
              </Select>

              <Select
                placeholder="Filter by Level"
                value={filters.agent_level}
                onChange={(value) => setFilters(prev => ({ ...prev, agent_level: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="1">Level 1</Select.Option>
                <Select.Option value="2">Level 2</Select.Option>
                <Select.Option value="3">Level 3</Select.Option>
              </Select>

              <Select
                placeholder="Filter by Status"
                value={filters.is_active}
                onChange={(value) => setFilters(prev => ({ ...prev, is_active: value }))}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>

              <Button 
                onClick={() => {
                  setFilters({ role: undefined, agent_level: undefined, is_active: undefined });
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                type="default"
              >
                Clear Filters
              </Button>
            </div>

              {/* Users Table */}
            <Table
                    columns={userColumns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loadingUsers}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                preserveSelectedRowKeys: true,
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || prev.pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({
                    ...prev,
                    current: 1,
                    pageSize: size,
                  }));
                },
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane tab={`Departments (${departments.length})`} key="2">
            <Table
              columns={deptColumns}
              dataSource={departments}
              rowKey="id"
              loading={loadingDepts}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} departments`,
              }}
            />
          </TabPane>
        </Tabs>

        {/* Create User Modal */}
        <Modal
          title="Create New User"
          open={createUserModal}
          onCancel={() => {
            setCreateUserModal(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Create User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>

            <Form.Item
              name="role"
                  label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
              initialValue="agent"
            >
              <Select>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="agent">Agent</Select.Option>
                <Select.Option value="developer">Developer</Select.Option>
                <Select.Option value="sales_agent">Sales Agent</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item
              name="department_ids"
              label="Departments"
            >
              <Select mode="multiple" placeholder="Select departments">
                {departments.map((dept: Department) => (
                  <Select.Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Select.Option>
                  ))}
                </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item name="agent_level" label="Agent Level" initialValue={1}>
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                  </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item 
                    name="can_edit_businesses" 
                    label="Can Add/Edit Businesses" 
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item name="ringcentral_extension" label="RingCentral Extension">
              <Input />
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Edit User Modal */}
        <Modal
          title="Edit User"
          open={editUserModal}
          onCancel={() => {
            setEditUserModal(false);
            setSelectedUser(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
          okText="Update User"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateUser}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="first_name"
                label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="last_name"
                label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>

            <Form.Item name="password" label="New Password (leave blank to keep current)">
              <Input.Password />
            </Form.Item>

            <Form.Item
              name="role"
                  label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="agent">Agent</Select.Option>
                <Select.Option value="developer">Developer</Select.Option>
                <Select.Option value="sales_agent">Sales Agent</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item name="department_ids" label="Departments">
              <Select mode="multiple" placeholder="Select departments">
                {departments.map((dept: Department) => (
                  <Select.Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Select.Option>
                  ))}
                </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item name="agent_level" label="Agent Level">
                    <Select>
                      <Select.Option value={1}>Level 1</Select.Option>
                      <Select.Option value={2}>Level 2</Select.Option>
                      <Select.Option value={3}>Level 3</Select.Option>
                  </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) =>
                getFieldValue('role') === 'agent' ? (
                  <Form.Item 
                    name="can_edit_businesses" 
                    label="Can Add/Edit Businesses" 
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item name="ringcentral_extension" label="RingCentral Extension">
              <Input />
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>

        {/* Create Department Modal */}
        <Modal
          title="Create Department"
          open={createDeptModal}
          onCancel={() => {
            setCreateDeptModal(false);
            deptForm.resetFields();
          }}
          onOk={() => deptForm.submit()}
          okText="Create Department"
        >
          <Form
            form={deptForm}
            layout="vertical"
            onFinish={handleCreateDept}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="name"
              label="Department Name"
              rules={[{ required: true, message: 'Please enter department name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </Content>
      {/* Bulk Upload Modal (match Businesses style) */}
      <Modal
        title="Bulk Upload Users"
        open={bulkUploadOpen}
        onCancel={() => setBulkUploadOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setBulkUploadOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="download"
            type="link"
            href="/templates/user_template.csv"
            download
          >
            Download Template
          </Button>,
        ]}
      >
        <p>Upload a CSV file with user data. Make sure to follow the template format.</p>
        <Upload.Dragger
          accept=".csv"
          beforeUpload={async (file) => {
            const formData = new FormData();
            formData.append('file', file as any);
            try {
              const res = await api.post('/api/users/bulk-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              const data = res.data || {};
              if (data.success) message.success(`Uploaded ${data.success} users`);
              if (Array.isArray(data.errors) && data.errors.length) message.error(`${data.errors.length} rows had errors`);
              queryClient.invalidateQueries('users');
              setBulkUploadOpen(false);
            } catch (e: any) {
              message.error(e?.response?.data?.error || 'Failed to upload');
            }
            return false;
          }}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to upload</p>
          <p className="ant-upload-hint">Only CSV files are supported</p>
        </Upload.Dragger>
      </Modal>

      {/* Confirm Bulk Delete */}
      <Modal
        title={`Confirm Bulk Delete (${selectedRowKeys.length} user(s))`}
        open={confirmBulkOpen}
        onCancel={() => setConfirmBulkOpen(false)}
        onOk={() => { bulkDeleteUsers.mutate(selectedRowKeys as string[]); setConfirmBulkOpen(false); }}
        okButtonProps={{ danger: true, disabled: confirmBulkText !== 'DELETE', children: 'Delete' }}
      >
        <Typography.Paragraph type="secondary">
          This action cannot be undone. Type DELETE to confirm.
        </Typography.Paragraph>
        <Input placeholder="Type DELETE" value={confirmBulkText} onChange={(e) => setConfirmBulkText(e.target.value)} />
      </Modal>

      {/* Confirm Password Reset */}
      <Modal
        title="Send Password Reset Link"
        open={passwordResetConfirm.open}
        onCancel={() => setPasswordResetConfirm({ open: false, user: null })}
        onOk={confirmSendPasswordReset}
        okText="Send Link"
        cancelText="Cancel"
        okButtonProps={{ loading: sendPasswordResetMutation.isLoading }}
      >
        {passwordResetConfirm.user && (
          <div>
            <Typography.Paragraph>
              Are you sure you want to send a password reset link to:
            </Typography.Paragraph>
            <Typography.Paragraph strong style={{ fontSize: 16, marginBottom: 8 }}>
              {passwordResetConfirm.user.first_name} {passwordResetConfirm.user.last_name}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              {passwordResetConfirm.user.email}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
              The reset link will be valid for 24 hours.
            </Typography.Paragraph>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default UsersAntd;

